import React, { useContext, useEffect, useRef, useState } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { getQueryGetDto } from '../../ServiceController';
import { NotificationManager, NotificationContainer } from 'react-notifications';
import { useDispatch } from 'react-redux';
import { RefertoContext } from 'app/context/RefertoContext';
import { dataDaSlashaDash } from 'app/utility/dataOra';
import { collectionDiagnosi } from '../collectionCode';

const StoriaFamiliare = ({ onClose, onCancel }) => {
    const dispatch = useDispatch();
    const { state: referto, dispatch: dispatchReferto } = useContext(RefertoContext);

    // Stato di default per il form
    const defaultState = {
        effectiveTime: "",
        diagnosisValue: "",
        diagnosisDisplayName: "",
        diagnosisId: "0",
        diagnosisAge: "0",
        deathAge: "0",
        gradoDiParentela: "MTH",
        gradoDiParentelaDescrizione: "Madre",
        sesso: "F",
    };

    const storiaFamiliare = referto?.storiaFamiliare || defaultState;

    // Elaborazione dei dati iniziali, includendo la conversione della data e il label per il grado di parentela
    const processedInitialData = {
        ...referto.storiaFamiliare,
        effectiveTime: storiaFamiliare?.effectiveTime ? dataDaSlashaDash(storiaFamiliare.effectiveTime) : '',
        gradoDiParentelaDescrizione:
            storiaFamiliare?.gradoDiParentela === 'MTH'
                ? 'Madre'
                : storiaFamiliare?.gradoDiParentela === 'FTH'
                    ? 'Padre'
                    : storiaFamiliare?.gradoDiParentela === 'SIB'
                        ? 'Fratello/sorella'
                        : '',
    };

    const [entryData, setEntryData] = useState(processedInitialData || defaultState);
    const [errors, setErrors] = useState({});
    const [diagnosiReferto, setDiagnosiReferto] = useState([]);
    const [selectedDiagnosi, setSelectedDiagnosi] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const searchTimeout = useRef(null);

    // Imposta il valore selezionato di default se esistono già diagnosi salvate
    useEffect(() => {
        if (storiaFamiliare.diagnosisValue && storiaFamiliare.diagnosisDisplayName) {
            const diagnosiSelect = [
                {
                    code: storiaFamiliare.diagnosisValue,
                    description: storiaFamiliare.diagnosisDisplayName,
                },
            ];
            setSelectedDiagnosi(diagnosiSelect);
        }
    }, [storiaFamiliare]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Mappa per il grado di parentela
        const gradoDiParentelaMap = {
            MTH: 'Madre',
            FTH: 'Padre',
            SIB: 'Fratello/sorella',
        };

        let updatedFields = { [name]: value };

        if (name === 'gradoDiParentela') {
            updatedFields.gradoDiParentelaDescrizione = gradoDiParentelaMap[value] || '';
        }

        setEntryData((prev) => ({
            ...prev,
            ...updatedFields,
        }));
    };

    const handleNestedInputChange = (e, field, value) => {
        setEntryData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!entryData.diagnosisDisplayName) {
            newErrors.diagnosisDisplayName = "Il campo 'diagnosi' è obbligatorio.";
        }
        if (!entryData.effectiveTime) {
            newErrors.effectiveTime = "Il campo 'Data esecuzione' è obbligatorio.";
        }
        // Verifica che almeno una delle età (diagnosi o decesso) sia valorizzata
        if (
            (entryData.diagnosisAge === '' || Number(entryData.diagnosisAge) === 0) &&
            (entryData.deathAge === '' || Number(entryData.deathAge) === 0)
        ) {
            newErrors.diagnosisAge = "L'età è obbligatoria.";
        }
        if (!entryData.diagnosisValue) {
            newErrors.diagnosisValue = "Il campo 'diagnosi' è obbligatorio.";
        }
        return newErrors;
    };

    const handleSubmit = (e) => {
        NotificationManager.listNotify.forEach(n => NotificationManager.remove({ id: n.id }));
        e.preventDefault();
        const newErrors = validateForm();
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            return;
        }
        dispatchReferto({
            type: "UPDATE_FIELD",
            field: "storiaFamiliare",
            payload: entryData
        });
        NotificationManager.success("Salvataggio avvenuto con successo", "Salva");
        setErrors({});
        if (onClose) {
            onClose();
        }
    };

    const handleSearch = (query) => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        searchTimeout.current = setTimeout(() => {
            if (query.trim() === '') return;
            setIsLoading(true);
            const payload = {
                nomeCollection: collectionDiagnosi,
                campo: "description",
                valore: query
            };
            dispatch(getQueryGetDto('MongoDb/GetRisultatiMongo', payload))
                .then((results) => {
                    let transformedResults = results && results.length > 0
                        ? results.map(r => r.dati)
                        : [];
                    if (transformedResults.length > 0) transformedResults = transformedResults.sort((a, b) => a.description.localeCompare(b.description));
                    setDiagnosiReferto(transformedResults);
                    setIsLoading(false);
                })
                .catch(error => {
                    console.error('Errore nel servizio MongoDB:', error);
                    setIsLoading(false);
                });
        }, 300);
    };

    const handleCancella = () => {
        setEntryData(defaultState);
        dispatchReferto({
            type: "UPDATE_FIELD",
            field: "storiaFamiliare",
            payload: defaultState
        });
        setErrors({});
        if (onCancel) {
            onCancel();
        }
    };

    return (
        <div className='container'>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-3 form-group mb-3">
                            <label htmlFor="effectiveTime">Data esecuzione</label>
                            <input
                                type="date"
                                id="effectiveTime"
                                name="effectiveTime"
                                autoComplete="off"
                                className={`form-control ${errors.effectiveTime ? 'is-invalid' : ''}`}
                                value={entryData.effectiveTime}
                                onChange={handleInputChange}
                            />
                            {errors.effectiveTime && (
                                <div className="invalid-feedback">{errors.effectiveTime}</div>
                            )}
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12 form-group mb-3">
                            <label htmlFor="Diagnosi">Diagnosi</label>
                            <AsyncTypeahead
                                id="idDiagnosi"
                                placeholder="Inserisci il nome o il codice della diagnosi..."
                                onSearch={handleSearch}
                                selectHintOnEnter={true}
                                labelKey="description"
                                selected={selectedDiagnosi}
                                onChange={(selected) => {
                                    if (selected.length > 0) {
                                        setSelectedItem({
                                            id: selected[0].id,
                                            iddiagnosireferto: 0,
                                            code: selected[0].code,
                                            description: selected[0].description,
                                            diagnosi: selected[0].description,
                                        });
                                        setSelectedDiagnosi(selected);
                                        handleNestedInputChange(null, 'diagnosisValue', selected[0].code);
                                        handleNestedInputChange(null, 'diagnosisDisplayName', selected[0].description);
                                    } else {
                                        setSelectedDiagnosi([]);
                                    }
                                }}
                                options={diagnosiReferto}
                                className={`control ${errors.diagnosisId ? 'is-invalid' : ''}`}
                            />
                            {errors.diagnosisDisplayName && (
                                <div className="invalid-feedback">{errors.diagnosisDisplayName}</div>
                            )}
                        </div>

                        <div className="col-md-2 form-group mb-3">
                            <label htmlFor="diagnosisAge">Età diagnosi</label>
                            <input
                                type="number"
                                id="diagnosisAge"
                                name="diagnosisAge"
                                autoComplete="off"
                                className={`form-control ${errors.diagnosisAge ? 'is-invalid' : ''}`}
                                value={entryData.diagnosisAge}
                                onChange={handleInputChange}
                            />
                            {errors.diagnosisAge && (
                                <div className="invalid-feedback">{errors.diagnosisAge}</div>
                            )}
                        </div>

                        <div className="col-md-2 form-group mb-3">
                            <label htmlFor="deathAge">Età del decesso</label>
                            <input
                                type="number"
                                id="deathAge"
                                name="deathAge"
                                autoComplete="off"
                                className="form-control"
                                value={entryData.deathAge}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="col-md-3 form-group mb-3">
                            <label htmlFor="gradoDiParentela">Grado di parentela</label>
                            <select
                                id="gradoDiParentela"
                                name="gradoDiParentela"
                                className="form-control"
                                value={entryData.gradoDiParentela}
                                onChange={handleInputChange}
                            >
                                <option value="">Seleziona</option>
                                <option value="MTH">Madre</option>
                                <option value="FTH">Padre</option>
                                <option value="SIB">Fratello/sorella</option>
                            </select>
                        </div>

                        <div className="col-md-3 form-group mb-3">
                            <label htmlFor="sesso">Sesso</label>
                            <select
                                id="sesso"
                                name="sesso"
                                className="form-control"
                                value={entryData.sesso}
                                onChange={handleInputChange}
                            >
                                <option value="">Seleziona</option>
                                <option value="F">Femmina</option>
                                <option value="M">Maschio</option>
                                <option value="U">Non specificato</option>
                            </select>
                        </div>
                    </div>

                    <div className="mt-3 d-flex justify-content-between">
                        <button type="submit" className="btn btn-primary">
                            Conferma
                        </button>
                        <button type="button" className="btn btn-danger text-white" onClick={handleCancella}>
                            Cancella
                        </button>
                    </div>
                </form>
            </div>
            <NotificationContainer />
        </div>
    );
};

export default StoriaFamiliare;
